import className from "classnames/bind";
import dayjs from "dayjs";
import {Link} from "react-router-dom";
import {FontAwesomeIcon} from "~/components";
import {useGetMySubscriptionQuery} from "~/reduxs/api/subscriptionApiSlice";
import style from "../style/Account.module.scss";

const cn = className.bind(style);

const nf = (n) => Number(n || 0).toLocaleString('vi-VN');

/** Màu thanh tiến trình theo mức đã dùng (xanh → cam → đỏ khi sắp hết). */
const barColor = (pct) => (pct >= 90 ? '#dc2626' : pct >= 70 ? '#f59e0b' : '#16a34a');

/** 1 đồng hồ hạn mức: nhãn + số còn lại + thanh tiến trình + ghi chú làm mới. */
function Meter({icon, label, used, limit, remaining, resetAt, special, specialText, foot}) {
	const rem = remaining != null ? remaining : Math.max(0, (limit || 0) - (used || 0));
	const pct = special || !limit ? 0 : Math.min(100, Math.round(((used || 0) / limit) * 100));

	return (
		<div className={cn('meter')}>
			<div className={cn('meter-top')}>
				<span className={cn('meter-icon')}><FontAwesomeIcon icon={icon} /></span>
				<span className={cn('meter-label')}>{label}</span>
			</div>

			<div className={cn('meter-figure')}>
				{special ? (
					<span className={cn('meter-special')}>{specialText}</span>
				) : (
					<>
						<span className={cn('meter-remaining')}>{nf(rem)}</span>
						<span className={cn('meter-of')}>còn lại / {nf(limit)}</span>
					</>
				)}
			</div>

			<div className={cn('meter-bar')}>
				<div
					className={cn('meter-fill')}
					style={{width: `${special ? 100 : pct}%`, background: special ? '#7c3aed' : barColor(pct)}}
				/>
			</div>

			<div className={cn('meter-foot')}>{foot}</div>
		</div>
	);
}

/**
 * Card "Gói & Hạn mức" trên trang hồ sơ: tên gói + giá + 3 đồng hồ hạn mức
 * (token AI/tháng, URL kiểm tra/tuần, dự án). Số liệu lấy từ subscription/me.
 */
function AccountPlanCard() {

	const {data: sub, isLoading} = useGetMySubscriptionQuery();

	if (isLoading || !sub) return null;

	const isRoot = Boolean(sub.is_root);
	const ownKey = Boolean(sub.require_own_ai_key);
	const tk = sub.ai_tokens || {};
	const url = sub.url_checks || {};
	const proj = sub.projects || {};
	// Gói trả phí: hạn mức theo chu kỳ mua — làm mới khi gia hạn, không tự reset theo lịch.
	const renew = sub.billing?.cycle === 'purchase';

	return (
		<section className={cn('card')}>
			<div className={cn('card-head')}>
				<span className={cn('card-head-icon')}>
					<i className="fa-light fa-gem" />
				</span>
				<h2 className={cn('card-title')}>Gói & Hạn mức</h2>

				<div className={cn('plan-badge')} data-plan={sub.plan?.slug}>
					<FontAwesomeIcon icon="fa-light fa-crown" />
					<span>{sub.plan?.name || 'Miễn phí'}</span>
					{sub.plan?.price > 0 && <em>{nf(sub.plan.price)}đ/tháng</em>}
				</div>

				<Link to="/subscription" className={cn('plan-link')}>
					Quản lý gói <FontAwesomeIcon icon="fa-light fa-arrow-right" />
				</Link>
			</div>

			<div className={cn('meter-grid')}>
				<Meter
					icon="fa-light fa-wand-magic-sparkles"
					label={renew ? 'Token AI / chu kỳ gói' : 'Token AI / tháng'}
					used={tk.used}
					limit={tk.limit}
					remaining={tk.remaining}
					special={isRoot || ownKey}
					specialText={isRoot ? 'Không giới hạn' : 'Khoá AI riêng'}
					foot={
						isRoot
							? 'Tài khoản quản trị'
							: ownKey
								? 'Dùng khoá AI của riêng bạn'
								: `Đã dùng ${nf(tk.used)}${tk.reset_at
									? (renew ? ' · làm mới khi gia hạn (hạn ' + dayjs(tk.reset_at).format('DD/MM') + ')' : ' · tự làm mới ' + dayjs(tk.reset_at).format('DD/MM'))
									: renew ? ' · làm mới khi gia hạn gói' : ''}`
					}
				/>

				<Meter
					icon="fa-light fa-magnifying-glass-chart"
					label="URL kiểm tra / tuần"
					used={url.used}
					limit={url.limit}
					remaining={url.remaining}
					special={isRoot}
					specialText="Không giới hạn"
					foot={
						isRoot
							? 'Tài khoản quản trị'
							: `Đã dùng ${nf(url.used)}${url.reset_at ? ' · tự làm mới ' + dayjs(url.reset_at).format('DD/MM') : ''}`
					}
				/>

				<Meter
					icon="fa-light fa-folder-open"
					label="Dự án"
					used={proj.used}
					limit={proj.limit}
					special={isRoot}
					specialText="Không giới hạn"
					foot={isRoot ? 'Tài khoản quản trị' : `Đang dùng ${nf(proj.used)} / ${nf(proj.limit)} dự án`}
				/>
			</div>
		</section>
	);
}

export default AccountPlanCard;
